/**
 * ResourcesPage: Ressourcen-Verwaltung.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiCallError, type Resource } from "../lib/api";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Label } from "../components/Label";
import { Drawer } from "../components/Drawer";
import { Badge } from "../components/Badge";
import { ListToolbar } from "../components/ListToolbar";
import { ActiveToggle } from "../components/ActiveToggle";
import { useAuth } from "../lib/auth";
import { t } from "../locales/de";

type FormState = {
  name: string;
  locationId: string;
  category: string;
  serialNo: string;
  active: boolean;
};

const EMPTY: FormState = {
  name: "",
  locationId: "",
  category: "",
  serialNo: "",
  active: true,
};

export function ResourcesPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [filterLoc, setFilterLoc] = useState("");
  const [filterQ, setFilterQ] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Resource | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const canWrite = user?.role === "admin" || user?.role === "planer";
  const canDelete = user?.role === "admin";

  const locQ = useQuery({ queryKey: ["locations"], queryFn: () => api.listLocations() });
  const locations = locQ.data?.locations ?? [];

  const resQ = useQuery({
    queryKey: ["resources", filterLoc, filterQ],
    queryFn: () => api.listResources({ locationId: filterLoc ? Number(filterLoc) : undefined, q: filterQ || undefined }),
  });
  const resources = resQ.data?.resources ?? [];

  const createMut = useMutation({
    mutationFn: (body: Partial<Resource>) => api.createResource(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["resources"] }); setDrawerOpen(false); },
    onError: (e) => setError(e instanceof ApiCallError ? e.message : "Fehler"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<Resource> }) => api.updateResource(id, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["resources"] }); setDrawerOpen(false); },
    onError: (e) => setError(e instanceof ApiCallError ? e.message : "Fehler"),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => api.deleteResource(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resources"] }),
    onError: (e) => alert(e instanceof ApiCallError ? e.message : "Löschen fehlgeschlagen"),
  });

  function openCreate() { setEditing(null); setForm(EMPTY); setError(null); setDrawerOpen(true); }
  function openEdit(r: Resource) {
    setEditing(r);
    setForm({
      name: r.name,
      locationId: r.locationId?.toString() ?? "",
      category: r.category ?? "",
      serialNo: r.serialNo ?? "",
      active: r.isActive,
    });
    setError(null);
    setDrawerOpen(true);
  }
  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const body = {
      name: form.name.trim(),
      locationId: form.locationId ? Number(form.locationId) : null,
      category: editing ? form.category.trim() : form.category.trim() || undefined,
      serialNo: editing ? form.serialNo.trim() : form.serialNo.trim() || undefined,
      active: form.active,
    };
    if (!body.name) return setError("Name ist erforderlich");
    if (editing) updateMut.mutate({ id: editing.id, body });
    else createMut.mutate(body);
  }

  const locName = (id: number | null) =>
    id == null ? "—" : locations.find((l) => l.id === id)?.name ?? `Standort ${id}`;
  const submitting = createMut.isPending || updateMut.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t.nav.resources}</h1>
        {canWrite && <Button onClick={openCreate}>+ Neue Ressource</Button>}
      </div>

      <ListToolbar searchValue={filterQ} onSearchChange={setFilterQ} searchPlaceholder="Ressourcen suchen…" resultCount={resources.length} hasActiveFilters={Boolean(filterQ || filterLoc)} onReset={() => { setFilterQ(""); setFilterLoc(""); }}>
        <select aria-label="Nach Standort filtern" value={filterLoc} onChange={(e) => setFilterLoc(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">Alle Standorte</option>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </ListToolbar>

      <div className="rounded-lg border border-slate-200 bg-white">
        {resQ.isLoading ? (
          <div className="p-6 text-sm text-slate-500">Lade…</div>
        ) : resQ.isError ? (
          <div className="p-6 text-sm text-rose-700">Ressourcen konnten nicht geladen werden. Bitte Seite neu laden.</div>
        ) : resources.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">Keine Ressourcen.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Kategorie</th>
                <th className="px-4 py-3">Serien-Nr.</th>
                <th className="px-4 py-3">Standort</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {resources.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-slate-900">{r.name}</td>
                  <td className="px-4 py-3 text-slate-600">{r.category ?? "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{r.serialNo ?? "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{locName(r.locationId)}</td>
                  <td className="px-4 py-3">{r.isActive ? <Badge variant="success">Aktiv</Badge> : <Badge variant="neutral">Inaktiv</Badge>}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {canWrite && (
                        <ActiveToggle
                          checked={r.isActive}
                          disabled={updateMut.isPending}
                          label={`Ressource ${r.name}`}
                          onChange={() => updateMut.mutate({ id: r.id, body: { isActive: !r.isActive } })}
                        />
                      )}
                      {canWrite && <Button variant="secondary" onClick={() => openEdit(r)}>Bearbeiten</Button>}
                      {canDelete && (
                        <Button variant="danger" onClick={() => {
                          if (confirm(`Ressource "${r.name}" wirklich löschen?`)) deleteMut.mutate(r.id);
                        }}>Löschen</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={editing ? "Ressource bearbeiten" : "Neue Ressource"}>
        <form onSubmit={submit} className="space-y-4">
          {error && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <Label htmlFor="loc2">Standort</Label>
            <select id="loc2" value={form.locationId} onChange={(e) => setForm({ ...form, locationId: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
              <option value="">— keinem Standort zugeordnet —</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="cat">Kategorie</Label>
            <Input id="cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="z. B. AV, Moderation" />
          </div>
          <div>
            <Label htmlFor="sn">Serien-Nr.</Label>
            <Input id="sn" value={form.serialNo} onChange={(e) => setForm({ ...form, serialNo: e.target.value })} />
          </div>
          <div className="flex items-center gap-2">
            <input id="active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
            <Label htmlFor="active" className="!mb-0">Aktiv</Label>
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
            <Button variant="secondary" type="button" onClick={() => setDrawerOpen(false)}>Abbrechen</Button>
            <Button type="submit" disabled={submitting}>{submitting ? "Speichere…" : editing ? "Speichern" : "Anlegen"}</Button>
          </div>
        </form>
      </Drawer>
    </div>
  );
}
